// src/engines/EngineLoader.jsx
// El loader inteligente. Muestra exactamente qué está construyendo la IA.
// Nunca parece trabado. Siempre está "pensando".

import { useState, useEffect, useRef } from "react";
import "./EngineLoader.css";

/* Ver la nota de `alpha` en EngineMode.jsx: pegar la opacidad en
   hexadecimal deja de funcionar en cuanto el color es una variable. */
const alpha = (color, pct) => `color-mix(in srgb, ${color} ${pct}%, transparent)`;

// ─── FASES DEL PENSAMIENTO ────────────────────────────────────────────────
// Cada fase tiene duración aproximada, icono, título y micro-mensajes rotativos.
// El sistema rota los mensajes internamente para que nunca se vea estático.

const PHASES = [
    {
        id: "analyzing",
        icon: "🔍",
        title: "Analizando tu clase",
        color: "var(--em-blue)",
        messages: [
            "Identificando el tema gramatical clave...",
            "Revisando tu nivel y contexto...",
            "Mapeando los puntos de dificultad...",
            "Seleccionando el módulo correcto...",
        ],
        minMs: 800,
    },
    {
        id: "grammar",
        icon: "📖",
        title: "Construyendo la Grammar Note",
        color: "var(--em-accent-ink)",
        messages: [
            "Redactando la regla principal...",
            "Generando ejemplos para tu contexto...",
            "Añadiendo el error más común y su corrección...",
            "Creando el truco de memoria...",
        ],
        minMs: 1200,
    },
    {
        id: "exercises",
        icon: "✏️",
        title: "Diseñando los 5 ejercicios",
        color: "var(--em-green)",
        messages: [
            "Ejercicio 1: listo ✓",
            "Ejercicio 2: listo ✓",
            "Ejercicio 3: calibrando dificultad...",
            "Ejercicio 4: ajustando vocabulario...",
            "Ejercicio 5: revisión final...",
        ],
        minMs: 2000,
    },
    {
        id: "calibrating",
        icon: "⚙️",
        title: "Calibrando para ti",
        color: "var(--em-purple)",
        messages: [
            "Ajustando vocabulario a tu nivel...",
            "Verificando que los ejemplos sean relevantes...",
            "Aplicando contexto personalizado...",
            "Revisando coherencia pedagógica...",
        ],
        minMs: 800,
    },
    {
        id: "ready",
        icon: "🎓",
        title: "¡Tu clase está lista!",
        color: "var(--em-green)",
        messages: [
            "Empaquetando los ejercicios...",
            "Todo listo. Preparando la vista...",
        ],
        minMs: 400,
    },
];

// ─── EDUCATIONAL TIPS (rotate while waiting) ─────────────────────────────
const TIPS = [
    { icon: "💡", text: "Native speakers use contractions 80% of the time. Formal grammar is for writing, not speaking." },
    { icon: "🧠", text: "Learning 3 new verbs a day for 30 days gives you the core vocabulary for everyday conversations." },
    { icon: "🔥", text: "The most effective method: listen, repeat, use. Don't just study rules." },
    { icon: "⚡", text: "Grammar structures become automatic through repeated practice, not memorization." },
    { icon: "🎯", text: "One mistake corrected in time is worth more than 10 perfect sentences nobody ever corrected." },
    { icon: "📻", text: "Listening to English for 15 minutes a day improves comprehension more than 2 hours of passive study." },
    { icon: "🗣️", text: "Fear of making mistakes is the single biggest obstacle to speaking English fluently." },
];

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────
export default function EngineLoader({ lesson, config, isDone, onComplete }) {
    const [phaseIdx, setPhaseIdx] = useState(0);
    const [msgIdx, setMsgIdx] = useState(0);
    const [tipIdx, setTipIdx] = useState(0);
    const [dots, setDots] = useState(".");
    const [particles, setParticles] = useState([]);
    const [orbs, setOrbs] = useState([]);
    const timersRef = useRef([]);

    const currentPhase = PHASES[Math.min(phaseIdx, PHASES.length - 1)];
    const completedCount = Math.min(phaseIdx, PHASES.length - 1);

    // ── Generar partículas y orbes decorativos al montar ──────────────────
    useEffect(() => {
        setParticles(
            Array.from({ length: 18 }, (_, i) => ({
                id: i,
                x: Math.random() * 100,
                y: Math.random() * 100,
                size: 2 + Math.random() * 3,
                delay: Math.random() * 3,
                dur: 3 + Math.random() * 4,
            }))
        );
        setOrbs(
            Array.from({ length: 3 }, (_, i) => ({
                id: i,
                x: 20 + i * 30,
                y: 20 + Math.random() * 60,
                size: 80 + Math.random() * 100,
                delay: i * 0.8,
            }))
        );
    }, []);

    // ── Avance automático de fases ────────────────────────────────────────
    useEffect(() => {
        const t = timersRef.current;

        // Dots animados
        const dotsTimer = setInterval(() => {
            setDots(d => d.length >= 3 ? "." : d + ".");
        }, 400);
        t.push(dotsTimer);

        // Rotación de mensajes dentro de cada fase
        const msgTimer = setInterval(() => {
            setMsgIdx(i => i + 1);
        }, 1400);
        t.push(msgTimer);

        // Rotación de tips
        const tipTimer = setInterval(() => {
            setTipIdx(i => (i + 1) % TIPS.length);
        }, 4500);
        t.push(tipTimer);

        // Avance de fases (sin importar si la IA terminó)
        const phaseTimers = PHASES.slice(0, -1).map((phase, idx) => {
            const delay = PHASES.slice(0, idx).reduce((acc, p) => acc + p.minMs, 0);
            const timer = setTimeout(() => {
                setPhaseIdx(idx + 1);
                setMsgIdx(0);
            }, delay + phase.minMs);
            t.push(timer);
            return timer;
        });

        return () => {
            t.forEach(clearInterval);
            t.forEach(clearTimeout);
            timersRef.current = [];
        };
    }, []);

    // ── Cuando la IA termina → esperar que la animación llegue a "ready" ──
    useEffect(() => {
        if (!isDone) return;
        // Saltar directo a la fase "ready" y esperar 800ms para la transición
        setPhaseIdx(PHASES.length - 1);
        const t = setTimeout(() => onComplete?.(), 900);
        return () => clearTimeout(t);
    }, [isDone]);

    const currentMsg = currentPhase.messages[msgIdx % currentPhase.messages.length];
    const currentTip = TIPS[tipIdx % TIPS.length];
    const progress = Math.min(((phaseIdx) / (PHASES.length - 1)) * 100, 100);

    return (
        <div className="el-root">

            {/* ── Fondo: orbes animados ────────────────────────────────────────── */}
            <div className="el-bg" aria-hidden="true">
                {orbs.map(orb => (
                    <div
                        key={orb.id}
                        className="el-orb"
                        style={{
                            left: `${orb.x}%`,
                            top: `${orb.y}%`,
                            width: orb.size,
                            height: orb.size,
                            animationDelay: `${orb.delay}s`,
                            background: [
                                "var(--em-particle-c)",
                                "var(--em-particle-a)",
                                "var(--em-particle-b)",
                            ][orb.id % 3],
                        }}
                    />
                ))}
                {particles.map(p => (
                    <div
                        key={p.id}
                        className="el-particle"
                        style={{
                            left: `${p.x}%`,
                            top: `${p.y}%`,
                            width: p.size,
                            height: p.size,
                            animationDuration: `${p.dur}s`,
                            animationDelay: `${p.delay}s`,
                            background: ["var(--em-accent-ink)", "var(--em-blue)", "var(--em-green)", "var(--em-purple)"][p.id % 4],
                        }}
                    />
                ))}
            </div>

            {/* ── Contenido centrado ───────────────────────────────────────────── */}
            <div className="el-content">

                {/* Brain / icono pulsante */}
                <div className="el-brain-wrapper" aria-hidden="true">
                    <div
                        className="el-brain-ring el-ring-3"
                        style={{ borderColor: `${alpha(currentPhase.color, 9)}` }}
                    />
                    <div
                        className="el-brain-ring el-ring-2"
                        style={{ borderColor: `${alpha(currentPhase.color, 19)}` }}
                    />
                    <div
                        className="el-brain-ring el-ring-1"
                        style={{ borderColor: `${alpha(currentPhase.color, 33)}` }}
                    />
                    <div
                        className="el-brain-core"
                        style={{ boxShadow: `0 0 32px ${alpha(currentPhase.color, 20)}` }}
                    >
                        <span
                            className="el-brain-icon"
                            key={currentPhase.id}
                        >
                            {currentPhase.icon}
                        </span>
                    </div>
                </div>

                {/* Clase que se está procesando */}
                {lesson?.title && (
                    <div className="el-lesson-tag">
                        <span className="el-lesson-dot" />
                        {lesson.title}
                    </div>
                )}

                {/* Fase actual */}
                <h2
                    className="el-phase-title"
                    key={currentPhase.id}
                    style={{ color: currentPhase.color }}
                >
                    {currentPhase.title}
                </h2>

                {/* Mensaje micro-detalle */}
                <p className="el-micro-msg" key={`${currentPhase.id}-${msgIdx}`}>
                    {currentMsg}{dots}
                </p>

                {/* ── Barra de progreso ───────────────────────────────────────────── */}
                <div className="el-progress-wrap">
                    <div
                        className="el-progress-bar"
                        style={{ width: `${progress}%`, background: currentPhase.color }}
                    />
                </div>

                {/* ── Checklist de fases ─────────────────────────────────────────── */}
                <div className="el-phases">
                    {PHASES.map((phase, i) => {
                        const done = i < phaseIdx;
                        const active = i === phaseIdx;
                        const pending = i > phaseIdx;
                        return (
                            <div
                                key={phase.id}
                                className={`el-phase-row ${active ? "el-phase-active" : ""} ${done ? "el-phase-done" : ""}`}
                            >
                                <div
                                    className="el-phase-dot"
                                    style={{
                                        background: done
                                            ? "var(--em-green)"
                                            : active
                                                ? phase.color
                                                : "var(--em-veil-08)",
                                        boxShadow: active
                                            ? `0 0 8px ${alpha(phase.color, 53)}`
                                            : "none",
                                    }}
                                >
                                    {done ? "✓" : active ? <span className="el-dot-pulse" /> : ""}
                                </div>
                                <span
                                    className="el-phase-name"
                                    style={{
                                        color: done
                                            ? "var(--em-green)"
                                            : active
                                                ? "var(--em-text)"
                                                : "var(--em-faint)",
                                    }}
                                >
                                    {phase.title}
                                </span>
                                {done && (
                                    <span className="el-phase-check">✓</span>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* ── Tip educativo ──────────────────────────────────────────────── */}
                <div className="el-tip" key={tipIdx}>
                    <span className="el-tip-icon">{currentTip.icon}</span>
                    <p className="el-tip-text">{currentTip.text}</p>
                </div>

                {/* Mensaje de paciencia */}
                <p className="el-patience">
                    Esto toma entre 8–15 segundos. Vale la pena esperar. 🔥
                </p>

            </div>
        </div>
    );
}

